import { useEffect, useMemo } from "react";
import type { Model } from "survey-core";
import { mergeSessionDraft } from "../helpers/SessionStorageHelpers";

function debounce<T extends (...args: unknown[]) => void>(fn: T, wait: number) {
  let t: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}

// The form only writes the fields of its own questions; other forms' and steps' answers in the draft are untouched.
export function saveSurveyAnswersToDraft(model: Model, storageKey: string) {
  const patch: Record<string, unknown> = {};
  model.getAllQuestions().forEach((question) => {
    if (question.name) patch[question.name] = model.data[question.name];
  });
  mergeSessionDraft(storageKey, patch);
}

export function useSurveySessionSaverHook(
  model: Model,
  storageKey: string,
  debounceMs = 250
) {
  const saveDebounced = useMemo(
    () => debounce(() => saveSurveyAnswersToDraft(model, storageKey), debounceMs),
    [model, storageKey, debounceMs]
  );

  useEffect(() => {
    model.onValueChanged.add(saveDebounced);
    return () => {
      model.onValueChanged.remove(saveDebounced);
    };
  }, [model, saveDebounced]);

  return {};
}
